import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import UploadZone from "@/components/UploadZone";
import Gallery from "@/components/Gallery";
import Lightbox from "@/components/Lightbox";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OWNER_KEY = "pc-owner-pin";

export default function App() {
  const [photos, setPhotos] = useState([]);
  const [dupesCaught, setDupesCaught] = useState(0);
  const [selected, setSelected] = useState(null);
  const [ownerPin, setOwnerPin] = useState(() => localStorage.getItem(OWNER_KEY) || "");

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await axios.get(`${API}/photos`);
    setPhotos(data.photos);
    setDupesCaught(data.duplicates_caught);
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const unlockOwner = async () => {
    const pin = window.prompt("Owner PIN — guests can browse and download, only you can remove photos:");
    if (!pin) return;
    try {
      await axios.get(`${API}/owner/check`, { headers: { "X-Owner-PIN": pin } });
      localStorage.setItem(OWNER_KEY, pin);
      setOwnerPin(pin);
      toast.success("Owner mode on — you can now remove photos");
    } catch {
      toast.error("That PIN didn't work");
    }
  };

  const handleDelete = async (photo) => {
    try {
      await axios.delete(`${API}/photos/${photo.id}`, { headers: { "X-Owner-PIN": ownerPin } });
      setPhotos((ps) => ps.filter((p) => p.id !== photo.id));
      setSelected((s) => (s && s.id === photo.id ? null : s));
      toast.success("Removed from the gallery");
    } catch {
      toast.error("Only the owner can remove photos");
      localStorage.removeItem(OWNER_KEY);
      setOwnerPin("");
    }
  };

  const totalBytes = photos.reduce((s, p) => s + (p.size || 0), 0);

  return (
    <div className="min-h-screen bg-[#F5F1E6] text-[#3C3527]">
      <Toaster position="bottom-right" />
      <Header count={photos.length} />
      <main>
        <Hero photos={photos} />
        <Gallery photos={photos} isOwner={!!ownerPin} onSelect={setSelected} onDelete={handleDelete} />
        <UploadZone count={photos.length} bytes={totalBytes} dupesCaught={dupesCaught} onUploaded={refresh} />
      </main>
      <Footer isOwner={!!ownerPin} onUnlock={unlockOwner} />
      <AnimatePresence>
        {selected && <Lightbox photo={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
